/**
 * Cache Manager for File Operations
 * Handles cache invalidation and state synchronization
 */

class CacheManager {
  constructor() {
    this.cache = new Map();
    this.cacheKeys = new Set();
    this.maxCacheSize = 100;
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Generate cache key for file listing
   */
  generateKey(path, accessToken = null) {
    return `files:${path}${accessToken ? `:${accessToken}` : ''}`;
  }

  /**
   * Get cached data
   */
  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      this.cacheKeys.delete(key);
      return null;
    }
    
    return item.data;
  }

  /**
   * Set cached data
   */
  set(key, data, ttl = this.defaultTTL) {
    // Remove oldest entries if cache is full
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cacheKeys.values().next().value;
      this.cache.delete(firstKey);
      this.cacheKeys.delete(firstKey);
    }

    const expiry = Date.now() + ttl;
    this.cache.set(key, { data, expiry });
    this.cacheKeys.delete(key); // Remove if exists
    this.cacheKeys.add(key); // Add to end
    
    // console.log('💾 Cache set:', key, 'data length:', Array.isArray(data) ? data.length : 'N/A');
  }

  /**
   * Invalidate cache for specific path
   */
  invalidate(path) {
    // console.log('🧹 Invalidating cache for path:', path);
    const keysToDelete = [];
    for (const key of this.cacheKeys) {
      if (key.startsWith(`files:${path}`)) {
        keysToDelete.push(key);
      }
    }
    
    // console.log('🗑️ Keys to delete:', keysToDelete);
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.cacheKeys.delete(key);
    });
    
    // console.log('✅ Cache invalidated, removed', keysToDelete.length, 'entries');
    return keysToDelete.length;
  }

  /**
   * Invalidate cache for path and parent paths
   */
  invalidateHierarchy(path) {
    let pathsInvalidated = 0;
    
    // Invalidate current path
    pathsInvalidated += this.invalidate(path);
    
    // Invalidate parent paths
    let currentPath = path;
    while (currentPath !== '/' && currentPath !== '') {
      const lastSlashIndex = currentPath.lastIndexOf('/');
      currentPath = lastSlashIndex <= 0 ? '/' : currentPath.substring(0, lastSlashIndex);
      pathsInvalidated += this.invalidate(currentPath);
    }
    
    return pathsInvalidated;
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    this.cacheKeys.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      keys: Array.from(this.cacheKeys)
    };
  }

  /**
   * Optimistically update cache after file operations
   */
  updateFileCache(path, operation, fileData) {
    // console.log('🔄 Cache Update:', { path, operation, fileData, cacheKeys: Array.from(this.cacheKeys) });
    
    const keysToUpdate = [];
    
    for (const key of this.cacheKeys) {
      if (key.startsWith(`files:${path}`)) {
        keysToUpdate.push(key);
      }
    }
    
    // console.log('📋 Keys to update:', keysToUpdate);
    
    keysToUpdate.forEach(key => {
      const cachedItem = this.cache.get(key); // Get the raw cache item
      // console.log('🗂️ Processing key:', key, 'cachedItem:', cachedItem);
      
      if (cachedItem && cachedItem.data && Array.isArray(cachedItem.data)) {
        let updatedFiles = [...cachedItem.data];
        
        switch (operation) {
          case 'delete':
            updatedFiles = updatedFiles.filter(file => file.name !== fileData.name);
            // console.log(`🗑️ Delete operation: ${originalLength} -> ${updatedFiles.length} files`);
            break;
          case 'add':
            // Check if file already exists
            if (!updatedFiles.some(file => file.name === fileData.name)) {
              updatedFiles.push(fileData);
              // console.log(`➕ Add operation: ${originalLength} -> ${updatedFiles.length} files`);
            }
            break;
          case 'update':
            const index = updatedFiles.findIndex(file => file.name === fileData.name);
            if (index !== -1) {
              updatedFiles[index] = { ...updatedFiles[index], ...fileData };
              // console.log(`✏️ Update operation: Updated file at index ${index}`);
            }
            break;
          default:
            console.warn('⚠️ Unknown operation:', operation);
            break;
        }
        
        // Update the cache with new data
        this.cache.set(key, {
          data: updatedFiles,
          expiry: cachedItem.expiry
        });
        
        // console.log('✅ Cache updated for key:', key, 'new length:', updatedFiles.length);
      } else {
        // console.log('❌ No valid cache data found for key:', key);
      }
    });
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

// Export utility functions
export const invalidateFileCache = (path) => cacheManager.invalidateHierarchy(path);
export const clearAllCache = () => cacheManager.clear();
export const getCacheStats = () => cacheManager.getStats();

export default cacheManager;
